import {
    Box,
    Heading,
    Text,
    Card,
    CardBody,
    Button,
    useDisclosure,
    CardFooter,
    Image,
    HStack,
    Stack,
    Menu,
    MenuButton,
    MenuItem,
    MenuList
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import DeleteChildModal from './DeleteChildModal'
import QrCodeModal from './QrCodeModal'
import { useState } from 'react'
import { FiCalendar, FiUser, FiTrash2, FiMoreVertical, FiEdit } from 'react-icons/fi'
import { MdOutlineBloodtype } from 'react-icons/md'
import { RiLockPasswordFill } from 'react-icons/ri'
import { GrView } from 'react-icons/gr'

export type ChildCardProps = {
    _id?: string
    firstName: string
    middleName?: string
    lastName: string
    dateOfBirth: string
    gender: string
    lastCheckinCode?: string
    bloodGroup?: string
    picture?: string
    childId: string
    qrCode: string
}

export default function ChildCard({
    childId,
    firstName,
    middleName,
    lastName,
    dateOfBirth,
    gender,
    lastCheckinCode,
    bloodGroup,
    picture = '',
    qrCode
}: ChildCardProps) {
    const navigate = useNavigate()
    const [getChildId, setGetChildId] = useState<string | undefined>('')
    const { isOpen, onOpen, onClose } = useDisclosure()
    const { isOpen: isOpenQr, onOpen: onOpenQr, onClose: onCloseQr } = useDisclosure()

    const handleOnClick = () => {
        onOpen()
        setGetChildId(childId)
    }
    const fullName = [firstName, middleName, lastName].filter(Boolean).join(' ')

    const downloadImage = async (
        imageSrc: string,
        imageName: string,
        forceDownload = false
    ) => {
        if (forceDownload) {
            const imageBlob = await fetch(imageSrc)
                .then((response) => response.arrayBuffer())
                .then((buffer) => new Blob([buffer], { type: 'image/jpeg' }))

            const link = document.createElement('a')
            link.href = URL.createObjectURL(imageBlob)
            link.download = imageName
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } else {
            const link = document.createElement('a')
            link.href = imageSrc
            link.download = imageName
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        }
    }

    const qrCodeImage: any = qrCode

    const downloadQrCodeImage = (childName: string) => {
        downloadImage(qrCodeImage, childName)
    }

    const handleEditClick = () => {
        navigate(`/user/parent/children-list/${childId}`)
    }

    return (
        <Box maxW={'full'}>
            <DeleteChildModal
                id={getChildId}
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={onClose}
                fullName={fullName}
                picture={picture}
            />
            <QrCodeModal
                qrCodeImage={qrCode}
                isOpen={isOpenQr}
                onOpen={onOpenQr}
                onClose={onCloseQr}
                name={fullName}
            />
            <Card mt={2} variant={'outline'} width={'full'} maxW="sm">
                <Image
                    objectFit="cover"
                    src={picture}
                    alt={fullName}
                    height="200px"
                    borderTopRightRadius={4}
                    borderTopLeftRadius={4}
                    fallbackSrc="https://via.placeholder.com/200"
                />
                <CardBody>
                    <Heading size="sm" py={2}>
                        {fullName}
                    </Heading>
                    <HStack gap={6} fontSize={'sm'}>
                        <HStack gap={2} key={gender}>
                            <FiUser color="gray" />
                            <Text>{gender || '-'}</Text>
                        </HStack>
                        <HStack gap={2}>
                            <FiCalendar color="gray" />
                            <Text>
                                {new Date(dateOfBirth).toLocaleDateString('en-GB')}
                            </Text>
                        </HStack>
                        <HStack gap={2}>
                            <MdOutlineBloodtype color="gray" />
                            <Text>{bloodGroup || '-'}</Text>
                        </HStack>
                    </HStack>
                    <Stack spacing={2} pt={4}>
                        <Text
                            color="gray"
                            fontWeight="semibold"
                            letterSpacing="wide"
                            fontSize="xs"
                            textTransform="uppercase"
                        >
                            Checkout Code
                        </Text>
                        <HStack>
                            <RiLockPasswordFill color={'#6D6AFF'} />
                            <Heading fontSize="sm" color={'brand.200'}>
                                {`${lastCheckinCode ? lastCheckinCode : '- - - - -'}`}
                            </Heading>
                        </HStack>
                    </Stack>
                </CardBody>
                <CardFooter justify="space-between" flexWrap="wrap">
                    <Button
                        variant={'outline'}
                        onClick={() => downloadQrCodeImage(fullName)}
                    >
                        Download QR code
                    </Button>
                    <Menu>
                        <MenuButton
                            as={Button}
                            aria-label="Options"
                            leftIcon={<FiMoreVertical />}
                            variant="ghost"
                        >
                            More
                        </MenuButton>
                        <MenuList>
                            <MenuItem icon={<GrView />} onClick={onOpenQr}>
                                View QR code
                            </MenuItem>
                            <MenuItem icon={<FiEdit />} onClick={handleEditClick}>
                                Edit info
                            </MenuItem>
                            <MenuItem
                                icon={<FiTrash2 />}
                                onClick={handleOnClick}
                                textColor={'red'}
                            >
                                Delete child
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </CardFooter>
            </Card>
        </Box>
    )
}
